import React, { useEffect, useState, useCallback } from "react";
import {
  ShieldCheck,
  Package,
  MessageSquare,
  LogOut,
  RefreshCw,
  DollarSign,
  Clock,
  Mail,
  KeyRound,
  Check,
  Lock,
  Ghost,
  Apple,
  Smartphone,
  Send,
} from "lucide-react";
import {
  adminLogin,
  adminGetStats,
  adminGetOrders,
  adminUpdateOrderStatus,
  adminGetContacts,
  adminUpdateContactHandled,
  adminGetWaitlist,
} from "../api";
import { formatAUD } from "../mock";
import { useToast } from "../hooks/use-toast";

const TOKEN_KEY = "phantom_admin_token";

const STATUS_OPTS = [
  { id: "pending", label: "Pending" },
  { id: "confirmed", label: "Confirmed" },
  { id: "shipped", label: "Shipped" },
  { id: "cancelled", label: "Cancelled" },
];

const PAY_STATUS_OPTS = [
  { id: "unpaid", label: "Unpaid" },
  { id: "paid_reported", label: "Paid reported" },
  { id: "confirmed", label: "Confirmed" },
];

const payStatusColor = (s) => {
  switch (s) {
    case "unpaid": return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    case "paid_reported": return "bg-blue-500/15 text-blue-300 border-blue-500/30";
    case "confirmed": return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    default: return "bg-white/10 text-white/70 border-white/20";
  }
};

const methodLabel = (m) => ({ xmr: "Monero", ltc: "Litecoin", bank: "Bank" }[m] || m);

const statusColor = (s) => {
  switch (s) {
    case "pending": return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    case "confirmed": return "bg-blue-500/15 text-blue-300 border-blue-500/30";
    case "shipped": return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    case "cancelled": return "bg-red-500/15 text-red-300 border-red-500/30";
    default: return "bg-white/10 text-white/70 border-white/20";
  }
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const refresh = useCallback(async (t) => {
    const tk = t || token;
    if (!tk) return;
    setLoading(true);
    try {
      const [s, o, c, w] = await Promise.all([
        adminGetStats(tk),
        adminGetOrders(tk),
        adminGetContacts(tk),
        adminGetWaitlist(tk),
      ]);
      setStats(s);
      setOrders(o.orders || []);
      setContacts(c.contacts || []);
      setWaitlist(w.waitlist || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
        setToken("");
        toast({ title: "Session expired", description: "Please log in again." });
      } else {
        toast({ title: "Failed to load", description: String(err?.message || err) });
      }
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    // If token exists in storage, try to auto-load
    if (token && !authed) {
      (async () => {
        try {
          await adminLogin(token);
          setAuthed(true);
          refresh(token);
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    if (!token || checking) return;
    setChecking(true);
    try {
      await adminLogin(token);
      localStorage.setItem(TOKEN_KEY, token);
      setAuthed(true);
      refresh(token);
      toast({ title: "Welcome", description: "Admin session opened." });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast({ title: "Access denied", description: String(msg) });
    } finally {
      setChecking(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setOrders([]);
    setContacts([]);
    setWaitlist([]);
    setStats(null);
  };

  const changeStatus = async (orderNumber, status) => {
    try {
      await adminUpdateOrderStatus(token, orderNumber, { status });
      setOrders((prev) =>
        prev.map((o) => (o.order_number === orderNumber ? { ...o, status } : o))
      );
      toast({ title: "Updated", description: `${orderNumber} → ${status}` });
      refresh();
    } catch (err) {
      toast({ title: "Update failed", description: String(err?.message || err) });
    }
  };

  const changePayStatus = async (orderNumber, payment_status) => {
    try {
      await adminUpdateOrderStatus(token, orderNumber, { payment_status });
      setOrders((prev) =>
        prev.map((o) =>
          o.order_number === orderNumber
            ? { ...o, payment_status, ...(payment_status === "confirmed" ? { status: "confirmed" } : {}) }
            : o
        )
      );
      toast({ title: "Payment updated", description: `${orderNumber} → ${payment_status}` });
      refresh();
    } catch (err) {
      toast({ title: "Update failed", description: String(err?.message || err) });
    }
  };

  const toggleHandled = async (contactId, handled) => {
    try {
      await adminUpdateContactHandled(token, contactId, handled);
      setContacts((prev) =>
        prev.map((c) => (c.id === contactId ? { ...c, handled } : c))
      );
    } catch (err) {
      toast({ title: "Update failed", description: String(err?.message || err) });
    }
  };

  // ---------------- Login screen ----------------
  if (!authed) {
    return (
      <main className="min-h-screen bg-black text-white pt-24 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="card-glass rounded-3xl p-8">
            <div className="chip mb-4">
              <Lock className="h-3 w-3" /> Restricted
            </div>
            <h1 className="font-headline text-4xl font-bold text-gradient-purple">
              Admin console.
            </h1>
            <p className="mt-3 text-sm text-white/60">
              Enter your admin token to review orders and contact messages.
            </p>

            <form onSubmit={doLogin} className="mt-6 space-y-4">
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-1.5">
                  Admin token
                </div>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <input
                    type="password"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    autoFocus
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm text-white focus:border-purple-500/60 outline-none"
                    placeholder="phantom-admin-…"
                  />
                </div>
              </label>
              <button type="submit" disabled={checking} className="btn-primary w-full justify-center">
                {checking ? "Checking…" : "Enter console"}
              </button>
            </form>
            <div className="mt-6 text-[11px] text-white/40 leading-relaxed">
              The default token is set in <code className="text-purple-300">backend/.env</code> as
              {" "}<code className="text-purple-300">ADMIN_TOKEN</code>. Change it before going
              live and restart the backend.
            </div>
          </div>
        </div>
      </main>
    );
  }

  // ---------------- Console ----------------
  return (
    <main className="min-h-screen bg-black text-white pt-24">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="chip">
              <ShieldCheck className="h-3 w-3" /> Admin console
            </div>
            <h1 className="mt-3 font-headline text-4xl md:text-5xl font-bold text-gradient-purple leading-[0.95]">
              Command deck.
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => refresh()} className="btn-ghost">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} className="btn-ghost text-red-300 hover:text-red-200">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-3">
            <Stat icon={Package} label="Orders" value={stats.orders_total} />
            <Stat icon={Clock} label="Pending" value={stats.orders_pending} accent />
            <Stat icon={DollarSign} label="Revenue" value={formatAUD(stats.revenue_aud || 0)} />
            <Stat icon={Mail} label="Messages" value={stats.contacts_total} />
            <Stat icon={MessageSquare} label="Open" value={stats.contacts_open} accent />
            <Stat icon={Ghost} label="Waitlist" value={stats.waitlist_total || 0} />
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-2 bg-white/5 border border-white/10 rounded-full p-1 w-fit">
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")}>
            <Package className="h-4 w-4" /> Orders ({orders.length})
          </TabBtn>
          <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")}>
            <MessageSquare className="h-4 w-4" /> Messages ({contacts.length})
          </TabBtn>
          <TabBtn active={tab === "waitlist"} onClick={() => setTab("waitlist")}>
            <Ghost className="h-4 w-4" /> Waitlist ({waitlist.length})
          </TabBtn>
        </div>

        {/* Orders */}
        {tab === "orders" && (
          <div className="mt-6 space-y-3">
            {orders.length === 0 && (
              <div className="card-glass rounded-2xl p-10 text-center text-white/50">
                No orders yet.
              </div>
            )}
            {orders.map((o) => (
              <div key={o.order_number} className="card-glass rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-display text-xl text-white">{o.order_number}</div>
                      <span className={`px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full border ${statusColor(o.status)}`}>
                        {o.status}
                      </span>
                      <span className={`px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full border ${payStatusColor(o.payment_status || "unpaid")}`}>
                        {(o.payment_status || "unpaid").replace("_", " ")}
                      </span>
                      <span className="px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full border bg-purple-500/10 text-purple-200 border-purple-500/30">
                        {methodLabel(o.payment_method || "bank")}
                      </span>
                    </div>
                    <div className="text-xs text-white/50 mt-1">
                      {new Date(o.created_at).toLocaleString("en-AU")} · {o.customer.name} · {o.customer.email}
                    </div>
                    {o.customer.address && (
                      <div className="text-xs text-white/40 mt-1">Ship: {o.customer.address}</div>
                    )}
                    {o.customer.notes && (
                      <div className="text-xs text-purple-200/70 mt-1">Note: {o.customer.notes}</div>
                    )}
                    {o.crypto_currency && (
                      <div className="mt-2 text-xs text-purple-300 font-mono">
                        Expects: {o.crypto_amount} {o.crypto_currency}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Total</div>
                    <div className="font-display text-2xl font-bold text-white">{formatAUD(o.total)}</div>
                    {o.discount_amount > 0 && (
                      <div className="text-[10px] text-purple-300 mt-0.5">−{formatAUD(o.discount_amount)} ({o.discount_percent}%)</div>
                    )}
                    <div className="mt-2 flex flex-col gap-1.5 items-end">
                      <select
                        value={o.status}
                        onChange={(e) => changeStatus(o.order_number, e.target.value)}
                        className="bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs text-white/80 focus:border-purple-500/60 outline-none"
                      >
                        {STATUS_OPTS.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
                      </select>
                      <select
                        value={o.payment_status || "unpaid"}
                        onChange={(e) => changePayStatus(o.order_number, e.target.value)}
                        className="bg-purple-500/5 border border-purple-500/30 rounded-full px-3 py-1.5 text-xs text-purple-200 focus:border-purple-500/60 outline-none"
                      >
                        {PAY_STATUS_OPTS.map((s) => (<option key={s.id} value={s.id}>Pay: {s.label}</option>))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mt-4 border-t border-white/10 pt-4 grid gap-2">
                  {o.items.map((it, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <div className="text-white/80">
                        {it.product_name} · {it.variant}
                        <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-purple-300">
                          {it.tier_name ? `${it.tier_name} · qty ${it.qty}` : `qty ${it.qty}`}
                        </span>
                      </div>
                      <div className="text-white font-display">{formatAUD(it.line_total)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contacts */}
        {tab === "contacts" && (
          <div className="mt-6 space-y-3">
            {contacts.length === 0 && (
              <div className="card-glass rounded-2xl p-10 text-center text-white/50">
                No messages yet.
              </div>
            )}
            {contacts.map((c) => (
              <div key={c.id} className="card-glass rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-display text-lg text-white">{c.subject}</div>
                      {c.handled && (
                        <span className="px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/30 inline-flex items-center gap-1">
                          <Check className="h-3 w-3" /> Handled
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-white/50 mt-1">
                      {new Date(c.created_at).toLocaleString("en-AU")} · {c.name} · {c.email}
                    </div>
                    <p className="mt-3 text-sm text-white/80 whitespace-pre-wrap break-words">
                      {c.message}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleHandled(c.id, !c.handled)}
                    className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                      c.handled
                        ? "bg-white/5 border-white/10 text-white/60 hover:text-white"
                        : "bg-purple-500/10 border-purple-500/40 text-purple-200 hover:bg-purple-500/20"
                    }`}
                  >
                    {c.handled ? "Mark open" : "Mark handled"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Waitlist */}
        {tab === "waitlist" && (
          <div className="mt-6 space-y-3" data-testid="admin-waitlist-panel">
            {waitlist.length === 0 && (
              <div className="card-glass rounded-2xl p-10 text-center text-white/50">
                No signups yet. When someone joins the Phantom X waitlist,
                they'll show up here.
              </div>
            )}
            {waitlist.map((w) => (
              <div key={w.id} className="card-glass rounded-2xl p-5" data-testid={`waitlist-row-${w.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full border bg-purple-500/10 text-purple-200 border-purple-500/30 inline-flex items-center gap-1">
                        {w.contact_method === "signal" && <MessageSquare className="h-3 w-3" />}
                        {w.contact_method === "telegram" && <Send className="h-3 w-3" />}
                        {w.contact_method === "email" && <Mail className="h-3 w-3" />}
                        {w.contact_method === "session" && <KeyRound className="h-3 w-3" />}
                        {w.contact_method === "other" && <Ghost className="h-3 w-3" />}
                        {w.contact_method}
                      </span>
                      <span className="px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full border bg-white/5 text-white/70 border-white/10 inline-flex items-center gap-1">
                        {w.platform === "ios" && <Apple className="h-3 w-3" />}
                        {w.platform === "android" && <Smartphone className="h-3 w-3" />}
                        {w.platform === "both" && <Ghost className="h-3 w-3" />}
                        {w.platform}
                      </span>
                      {w.notified && (
                        <span className="px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/30 inline-flex items-center gap-1">
                          <Check className="h-3 w-3" /> Notified
                        </span>
                      )}
                    </div>
                    <div className="mt-2 font-mono text-purple-300 break-all">
                      {w.contact_handle}
                    </div>
                    <div className="text-xs text-white/50 mt-1">
                      {new Date(w.created_at).toLocaleString("en-AU")}
                    </div>
                    {w.note && (
                      <div className="mt-3 text-sm text-white/70 whitespace-pre-wrap break-words border-l-2 border-purple-500/40 pl-3">
                        {w.note}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className={`card-glass rounded-2xl p-4 ${accent ? "ring-1 ring-purple-500/30" : ""}`}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className="mt-2 font-display text-2xl font-bold text-white">{value}</div>
  </div>
);

const TabBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
      active ? "bg-purple-500 text-black" : "text-white/70 hover:text-white"
    }`}
  >
    {children}
  </button>
);

export default Admin;
